"use client"

import { useState } from "react"
import { motion } from "framer-motion"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { MapPin, Truck, Clock, Star, ArrowRight, CheckCircle } from "lucide-react"
import Link from "next/link"
import Image from "next/image"
import { cityData, cityImages } from "@/lib/cities-data"
import { CONTACT } from "@/lib/constants"
import { RequestLocationModal } from "@/components/request-location-modal"

export function LocationsList() {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.1
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 30, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { duration: 0.6, ease: "easeOut" as const }
    },
  }

  const statsVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.5, ease: "easeOut" as const }
    }
  }

  return (
    <section
      className="relative py-20 sm:py-28 overflow-hidden"
      aria-labelledby="locations-heading"
    >
      {/* Modern gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-blue-50/30" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_25%,rgba(37,99,235,0.05),transparent_50%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_75%_75%,rgba(249,115,22,0.05),transparent_50%)]" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <motion.header
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <motion.div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-blue-100 to-orange-100 text-blue-700 font-medium text-sm mb-6"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            <MapPin size={16} />
            PAN India Coverage
          </motion.div>

          <h1
            id="locations-heading"
            className="text-4xl sm:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-gray-900 via-blue-900 to-orange-900 bg-clip-text text-transparent mb-6"
          >
            Our Service Locations
          </h1>

          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed mb-8">
            Bigwin Packers & Movers operates across 500+ cities in India, delivering professional
            relocation services with local expertise and nationwide reliability.
          </p>

          {/* Quick Stats */}
          <motion.div
            className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {[
              { number: "500+", label: "Cities Covered", icon: MapPin },
              { number: "50,000+", label: "Successful Moves", icon: Truck },
              { number: "4.9/5", label: "Customer Rating", icon: Star },
              { number: "24/7", label: "Support Available", icon: Clock }
            ].map((stat, index) => {
              const Icon = stat.icon
              return (
                <motion.div
                  key={index}
                  variants={statsVariants}
                  className="text-center group"
                >
                  <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-gradient-to-br from-blue-500/10 to-orange-500/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <Icon size={20} className="text-blue-600" />
                  </div>
                  <div className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-blue-600 to-orange-600 bg-clip-text text-transparent mb-1">
                    {stat.number}
                  </div>
                  <div className="text-sm text-gray-600 font-medium">{stat.label}</div>
                </motion.div>
              )
            })}
          </motion.div>
        </motion.header>

        {/* Cities Grid */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
        >
          {cityData.map((city, index) => (
            <motion.article
              key={city.id}
              variants={itemVariants}
              className="group"
            >
              <Link href={`/locations/${city.slug}`} className="block">
                <Card className="relative p-8 h-full bg-white/90 backdrop-blur-sm border-0 shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden">
                  {/* Hover gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 to-orange-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

                  {/* Popular badge for featured cities */}
                  {index < 3 && (
                    <div className="absolute top-4 right-4 px-3 py-1 rounded-full bg-gradient-to-r from-orange-500 to-red-500 text-white text-xs font-semibold z-10">
                      Popular
                    </div>
                  )}

                  <div className="relative z-10 h-full flex flex-col">
                    {/* Header with Icon-style Image */}
                    <div className="flex items-start gap-4 mb-6">
                      {/* Modern Icon-style City Image */}
                      <motion.div
                        className="relative flex-shrink-0"
                        whileHover={{ scale: 1.05, rotate: 2 }}
                        transition={{ duration: 0.3 }}
                      >
                        <div className="relative w-20 h-20 rounded-2xl overflow-hidden shadow-lg ring-2 ring-blue-100 group-hover:ring-blue-300 transition-all duration-300">
                          <Image
                            src={cityImages[city.slug] || "/images/cities/default.jpg"}
                            alt={`${city.name} icon`}
                            fill
                            className="object-cover"
                            sizes="80px"
                          />
                          {/* Subtle gradient overlay */}
                          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-orange-500/10" />
                        </div>
                        {/* Decorative corner accent */}
                        <div className="absolute -top-1 -right-1 w-3 h-3 bg-gradient-to-br from-blue-500 to-orange-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      </motion.div>

                      {/* City Name and State */}
                      <div className="flex-1 min-w-0">
                        <motion.div
                          className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-100 to-orange-100 px-3 py-1 rounded-full mb-3"
                          whileHover={{ scale: 1.05 }}
                          transition={{ duration: 0.2 }}
                        >
                          <MapPin size={14} className="text-blue-600" />
                          <span className="text-sm font-semibold text-blue-700">{city.state}</span>
                        </motion.div>

                        <h2 className="text-2xl font-bold text-gray-900 mb-2 group-hover:text-blue-900 transition-colors">
                          {city.name}
                        </h2>
                      </div>
                    </div>
                    {/* Description */}
                    <div className="mb-6">
                      <p className="text-gray-600 leading-relaxed line-clamp-3">
                        {city.description}
                      </p>
                    </div>

                    {/* Enhanced Stats */}
                    <div className="grid grid-cols-2 gap-4 mb-6 p-4 rounded-xl bg-gradient-to-r from-gray-50 to-blue-50/50">
                      {city.localStats.slice(0, 2).map((stat, idx) => (
                        <div key={idx} className="text-center">
                          <div className="text-xl font-bold bg-gradient-to-r from-blue-600 to-orange-600 bg-clip-text text-transparent mb-1">
                            {stat.value}
                          </div>
                          <div className="text-xs text-gray-600 font-medium">{stat.label}</div>
                        </div>
                      ))}
                    </div>

                    {/* Services with icons */}
                    <div className="mb-6">
                      <div className="flex items-center gap-2 mb-3">
                        <CheckCircle size={16} className="text-green-500" />
                        <span className="text-sm font-semibold text-gray-700">Available Services</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {city.services.slice(0, 3).map((service, idx) => (
                          <span
                            key={idx}
                            className="text-xs bg-gradient-to-r from-blue-50 to-orange-50 text-blue-700 px-3 py-1 rounded-full border border-blue-100"
                          >
                            {service}
                          </span>
                        ))}
                        {city.services.length > 3 && (
                          <span className="text-xs text-gray-500 px-2 py-1">
                            +{city.services.length - 3} more
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Enhanced CTA */}
                    <motion.div
                      className="mt-auto"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <Button className="w-full bg-gradient-to-r from-blue-600 to-orange-600 hover:from-blue-700 hover:to-orange-700 text-white font-semibold py-3 rounded-xl shadow-lg group-hover:shadow-xl transition-all duration-300">
                        <span className="flex items-center justify-center gap-2">
                          View {city.name} Services
                          <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                        </span>
                      </Button>
                    </motion.div>
                  </div>

                  {/* Subtle border animation */}
                  <div className="absolute inset-0 rounded-lg border-2 border-transparent group-hover:border-gradient-to-r group-hover:from-blue-200 group-hover:to-orange-200 transition-all duration-500" />
                </Card>
              </Link>
            </motion.article>
          ))}
        </motion.div>

        {/* Bottom CTA Section */}
        <motion.div
          className="mt-20 text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.6 }}
          viewport={{ once: true }}
        >
          <div className="max-w-4xl mx-auto p-8 rounded-2xl bg-gradient-to-br from-blue-50 to-orange-50 border border-blue-100">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Don't See Your City Listed?
            </h2>
            <p className="text-lg text-gray-600 mb-6">
              We're expanding rapidly! Contact us to check if we serve your area or to request service in your city.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button
                  onClick={() => setIsModalOpen(true)}
                  className="px-6 py-3 bg-gradient-to-r from-blue-600 to-orange-600 text-white font-semibold rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  Request New Location
                </Button>
              </motion.div>

              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button
                  asChild
                  variant="outline"
                  className="px-6 py-3 bg-white text-blue-600 font-semibold rounded-full border-2 border-blue-200 hover:bg-blue-50 transition-all duration-300"
                >
                  <a href={`tel:${CONTACT.phone.display}`} className="hover:text-blue-700 transition-colors">
                    Call: {CONTACT.phone.display}
                  </a>
                </Button>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Request Location Modal */}
      <RequestLocationModal open={isModalOpen} onOpenChange={setIsModalOpen} />
    </section>
  )
}
